import { ApolloError, ApolloQueryResult, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { FLOWS_PROJECT_ROLE_BINDINGS_WITH_ROLES, FlowsProjectRoleBindingsData } from "./gql";
import { FlowsProjectRoleBindingView } from "./types";

type UseFlowsProjectProps = {
  projectId: string;
};

type UseFlowsProjectReturn = {
  project: { id: string; name: string } | undefined;
  roleBindings: FlowsProjectRoleBindingView[];
  roles: Array<{ id: string; name: string }>;
  loading: boolean;
  hasData: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<ApolloQueryResult<FlowsProjectRoleBindingsData>>;
};

const useFlowsProject = ({ projectId }: UseFlowsProjectProps): UseFlowsProjectReturn => {
  const { data, loading, error, refetch } = useQuery(FLOWS_PROJECT_ROLE_BINDINGS_WITH_ROLES);

  const project = useMemo(
    () => data?.flowsProjects.find((item) => item.id === projectId),
    [data, projectId]
  );

  return useMemo(
    () => ({
      project: project ? { id: project.id, name: project.name } : undefined,
      roleBindings: project?.roleBindings ?? [],
      roles: data?.roles ?? [],
      loading,
      hasData: !!data?.flowsProjects,
      error,
      refetch,
    }),
    [project, data, loading, error, refetch]
  );
};

export default useFlowsProject;
